import { buildApiUrl } from '../../utils/api';
import { ENTERED_PINCODE_STORAGE_KEY, getActivePincode } from '../../utils/location';

// localStorage key — bump version if schema changes
const LOCATION_STORAGE_KEY = 'kkisan_location_v1';

const loadPersistedLocation = () => {
  try {
    const raw = localStorage.getItem(LOCATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Basic shape validation
    if (parsed && typeof parsed === 'object' && parsed.pincode) return parsed;
  } catch {
    // ignore corrupted data
  }
  return null;
};

const persistLocation = (loc) => {
  try {
    if (loc) {
      localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(loc));
    } else {
      localStorage.removeItem(LOCATION_STORAGE_KEY);
    }
  } catch {
    // ignore storage errors (private browsing, quota)
  }
};

const persistEnteredPincode = (pincode) => {
  try {
    if (pincode) localStorage.setItem(ENTERED_PINCODE_STORAGE_KEY, pincode);
    else localStorage.removeItem(ENTERED_PINCODE_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
};

const loadEnteredPincode = () => {
  try {
    const pin = localStorage.getItem(ENTERED_PINCODE_STORAGE_KEY);
    return pin && /^\d{6}$/.test(pin.trim()) ? pin.trim() : null;
  } catch {
    return null;
  }
};

/**
 * Normalize the /location/resolve API response into a consistent shape.
 * Handles both pincode-based and lat/lng-based responses.
 */
/**
 * Normalize the full /location/resolve API response.
 * The API returns nested location/store/zone/delivery objects.
 */
const normalizeLocationData = (data = {}) => ({
  // Location fields — may be in nested `location` object or at top level
  pincode:     data.location?.pincode  ?? data.pincode     ?? data.postal_code  ?? null,
  area:        data.location?.area_name ?? data.area_name   ?? data.area        ?? data.locality    ?? null,
  city:        data.location?.city_name ?? data.city_name   ?? data.city        ?? null,
  state:       data.location?.state     ?? data.state_name  ?? data.state       ?? null,
  latitude:    data.location?.latitude  ?? data.latitude    ?? null,
  longitude:   data.location?.longitude ?? data.longitude   ?? null,
  // Serviceability
  serviceable:        data.serviceable        ?? data.is_serviceable    ?? data.delivery_available ?? false,
  delivery_available: data.delivery_available ?? data.serviceable       ?? false,
  resolved_by:        data.resolved_by        ?? null,
  // Store matched to this location
  store: data.store ?? null,   // { store_id, store_name, hub_id }
  // Delivery zone
  zone:  data.zone  ?? null,   // { zone_id, zone_name, zone_level }
  // Delivery config
  delivery: data.delivery ?? null, // { delivery_charge, free_delivery_min_amount, cod_available }
  resolvedAt: Date.now(),
});

const callResolveApi = async (params, authToken = null) => {
  const qs = new URLSearchParams(params).toString();
  const url = buildApiUrl(`api/v1/customer/location/resolve?${qs}`);
  const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
  const res  = await fetch(url, { headers });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.message || 'Could not resolve location');
  }
  // success:false means unserviceable — return data so caller can handle it
  return { ok: json.success !== false, data: json.data ?? json, message: json.message ?? '' };
};

export const createLocationSlice = (set, get) => ({
  // ── State ──────────────────────────────────────────────────────────────────
  userLocation:          loadPersistedLocation(), // null if not set yet
  enteredPincode:        loadEnteredPincode(),
  location_loading:      false,
  location_error:        null,
  location_unserviceable_message: null,
  // Controls the onboarding modal visibility:
  // Show if no location persisted. Never auto-open again after user acts.
  location_modal_open:   !loadPersistedLocation(),

  // ── Actions ────────────────────────────────────────────────────────────────

  openLocationModal:  () => set({ location_modal_open: true }),
  closeLocationModal: () => set({ location_modal_open: false }),

  clearLocation: () => {
    persistLocation(null);
    persistEnteredPincode(null);
    set({ userLocation: null, enteredPincode: null, location_modal_open: true, location_error: null, location_unserviceable_message: null });
  },

  /**
   * Resolve location from a 6-digit pincode.
   * Returns the normalized location object, or null if unserviceable/error.
   */
  resolveByPincode: async (pincode) => {
    const pin = String(pincode).trim();
    if (!/^\d{6}$/.test(pin)) {
      set({ location_error: 'Please enter a valid 6-digit pincode.' });
      return null;
    }
    persistEnteredPincode(pin);
    set({ enteredPincode: pin, location_loading: true, location_error: null, location_unserviceable_message: null });
    try {
      const { ok, data, message } = await callResolveApi({ pincode: pin });
      const loc = normalizeLocationData({ ...data, pincode: pin });
      if (!ok || !loc.serviceable) {
        set({
          location_loading: false,
          location_unserviceable_message: message || `Delivery not available for pincode ${pin}.`,
        });
        return null;
      }
      persistLocation(loc);
      set({ userLocation: loc, enteredPincode: pin, location_loading: false, location_modal_open: false, location_unserviceable_message: null });
      // Trigger catalog refresh so stock matches resolved store/pincode
      try {
        get().fetchProducts?.(get().activeCategorySlug, loc);
      } catch {}
      return loc;
    } catch (err) {
      set({ location_error: err.message || 'Could not resolve pincode.', location_loading: false });
      return null;
    }
  },

  /**
   * Resolve location from browser GPS coordinates.
   * Returns the normalized location object, or null if unserviceable/error.
   */
  resolveByCoords: async (latitude, longitude) => {
    set({ location_loading: true, location_error: null, location_unserviceable_message: null });
    try {
      const { ok, data, message } = await callResolveApi({
        latitude:  Number(latitude).toFixed(6),
        longitude: Number(longitude).toFixed(6),
      });
      const loc = normalizeLocationData({ ...data, latitude, longitude });
      if (!ok || !loc.serviceable) {
        set({
          location_loading: false,
          location_unserviceable_message: message || 'Delivery not available for your current location.',
        });
        return null;
      }
      persistLocation(loc);
      const resolvedPin = loc.pincode ? String(loc.pincode).trim() : null;
      persistEnteredPincode(resolvedPin);
      set({ userLocation: loc, enteredPincode: resolvedPin, location_loading: false, location_modal_open: false, location_unserviceable_message: null });
      // Trigger catalog refresh so stock matches resolved store/pincode
      try {
        get().fetchProducts?.(get().activeCategorySlug, loc);
      } catch {}
      return loc;
    } catch (err) {
      set({ location_error: err.message || 'Could not resolve location.', location_loading: false });
      return null;
    }
  },

  /**
   * Resolve location from a saved customer address_id (highest-priority per API docs).
   * Requires an auth token.
   * Returns the normalized location object, or null if unserviceable/error.
   */
  resolveByAddressId: async (address_id) => {
    if (!address_id) return null;
    set({ location_loading: true, location_error: null, location_unserviceable_message: null });
    try {
      const { getCookie } = await import('../../utils/cookies');
      const token = getCookie('authToken');
      const { ok, data, message } = await callResolveApi({ address_id: Number(address_id) }, token);
      const loc = normalizeLocationData(data);
      if (!ok || !loc.serviceable) {
        set({
          location_loading: false,
          location_unserviceable_message: message || 'Delivery not available for this address.',
        });
        return null;
      }
      persistLocation(loc);
      const resolvedPin = loc.pincode ? String(loc.pincode).trim() : null;
      persistEnteredPincode(resolvedPin);
      set({ userLocation: loc, enteredPincode: resolvedPin, location_loading: false, location_modal_open: false, location_unserviceable_message: null });
      // Trigger catalog refresh so stock matches resolved store/pincode
      try {
        get().fetchProducts?.(get().activeCategorySlug, loc);
      } catch {}
      return loc;
    } catch (err) {
      set({ location_error: err.message || 'Could not resolve address.', location_loading: false });
      return null;
    }
  },

  /**
   * Convenience: get the best available pincode from state
   * (user addresses → userLocation → localStorage → null).
   */
  getResolvedPincode: () => {
    return getActivePincode(get());
  },
});
